import { useEffect } from 'react';
import { View, Pressable, Text, Platform, StatusBar } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { monkeyWorldsViewStyles as styles } from './MonkeyWorldsView.styles';
import { MonkeyWorldsWebView } from './MonkeyWorldsWebView';
import { MonkeyWorldsOverlays } from './MonkeyWorldsOverlays';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useMonkeyWorldsGame } from '../../../src/quiz/hooks/useMonkeyWorldsGame';
import { MONKEY_WORLDS_TEST_IDS } from '../../../src/quiz/constants/monkeyWorlds.constants';

interface MonkeyWorldsViewProps {
  onMoreGames: () => void;
}

/**
 * Fullscreen host for the Monkey Worlds runtime (four sport worlds, canvas 2D platformer). The
 * runtime is a full-viewport canvas + in-canvas world map/HUD that sizes itself to whatever box it
 * gets; the host only provides the back control and the loading/error overlays.
 */
export function MonkeyWorldsView({ onMoreGames }: MonkeyWorldsViewProps) {
  const insets = useSafeAreaInsets();
  const game = useMonkeyWorldsGame({ insetTop: insets.top });

  // Hide status bar on native while the game is open.
  useEffect(() => {
    if (Platform.OS !== 'web') StatusBar.setHidden(true, 'fade');
    return () => { if (Platform.OS !== 'web') StatusBar.setHidden(false, 'fade'); };
  }, []);

  // Back leaves immediately, closing the funnel first (the runtime keeps its own map/retry screens).
  const handleBack = () => {
    game.confirmExit();
    onMoreGames();
  };

  return (
    <View style={styles.fullscreenContainer} testID={MONKEY_WORLDS_TEST_IDS.view}>
      {/* Back arrow — always top-left, on top of everything */}
      <Pressable
        testID={MONKEY_WORLDS_TEST_IDS.back}
        style={({ pressed }) => [styles.backButton, pressed && styles.pressedDim, { top: Math.max(8, insets.top) }]}
        onPress={handleBack}
      >
        <Text style={styles.backArrow}>←</Text>
      </Pressable>

      <View style={styles.canvasWrap}>
        <MonkeyWorldsWebView
          ref={(r) => game.bindWebView(r)}
          html={game.html}
          onMessage={game.onMessage}
        />
      </View>

      <MonkeyWorldsOverlays status={game.status} onRetry={game.retry} />
      <WarmupToast message={game.toast} />
    </View>
  );
}
